import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Account, AccountPosition, AccountActivityStatus } from '../types';
import apiService from '../services/api';
import { useSSE } from '../hooks/useSSE';
import PositionCard from '../components/PositionCard';
import SchwabOrphanCard, {
  DashboardSchwabOrphan,
} from '../components/SchwabOrphanCard';
import PositionDetailsModal from '../components/PositionDetailsModal';
import ClosePositionConfirmationModal from '../components/ClosePositionConfirmationModal';
import RecommendationCard from '../components/RecommendationCard';
import TierThesisWrapper from '../components/portfolio/TierThesisWrapper';
import DashboardTierHero from './dashboard/DashboardTierHero';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import TierPositionSection from '../components/portfolio/TierPositionSection';
import { useTierPortfolio } from '../hooks/useTierPortfolio';
import {
  buildTierSpectrum,
  positionMatchesTierFilter,
  resolveRiskTierForPosition,
} from '../tiers/resolveTierContext';
import { TIER_CATALOG } from '../tiers/tierCatalog';
import { TierBudgetSnapshot } from '../types/tier1Portfolio';
import { ActivityStatusIndicator } from '../components/ActivityStatusIndicator';
import {
  Wallet,
  TrendingUp,
  Target,
  PieChart as PieIcon,
  ActivitySquare,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const Dashboard: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activityStatusesByAccount, setActivityStatusesByAccount] = useState<
    Record<string, AccountActivityStatus[]>
  >({});
  const [activePositions, setActivePositions] = useState<AccountPosition[]>([]);
  const [schwabOrphans, setSchwabOrphans] = useState<DashboardSchwabOrphan[]>(
    [],
  );
  const [closingOrphanKey, setClosingOrphanKey] = useState<string | null>(null);
  const [orphanActionMessage, setOrphanActionMessage] = useState<string | null>(
    null,
  );
  const [positionActivityLogs, setPositionActivityLogs] = useState<
    Record<string, any[]>
  >({});
  const [positionAiReviews, setPositionAiReviews] = useState<
    Record<string, any>
  >({});
  const [positionPrices, setPositionPrices] = useState<Record<string, number>>(
    {},
  );
  const [recommendations, setRecommendations] = useState<AccountPosition[]>([]);
  const [pendingPositions, setPendingPositions] = useState<AccountPosition[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] =
    useState<AccountPosition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<AccountPosition | null>(null);
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] =
    useState(false);
  const [positionToClose, setPositionToClose] =
    useState<AccountPosition | null>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [schwabAuthStatus, setSchwabAuthStatus] = useState<{
    authenticated: boolean;
    tokenExpired: boolean;
    expiresAt?: string;
  } | null>(null);
  const [accountPerformanceHistory, setAccountPerformanceHistory] = useState<
    any[]
  >([]);
  const [intradayPerformanceData, setIntradayPerformanceData] = useState<any[]>(
    [],
  );
  const [intradayTimeframe, setIntradayTimeframe] = useState<
    '1d' | '1w' | '1m' | '1y' | 'custom'
  >('1w');
  const [customFromDate, setCustomFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [customToDate, setCustomToDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [appliedCustomRange, setAppliedCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [matchingArticles, setMatchingArticles] = useState<any[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const { onAccountUpdate, onBalanceChange } = useSSE();
  const { tierFilter, selectedAccountId, accountContext } = useTierPortfolio();

  const accountById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a])),
    [accounts],
  );

  const matchesScopeAndTier = useCallback(
    (position: AccountPosition) => {
      if (selectedAccountId && position.accountId !== selectedAccountId) {
        return false;
      }
      const acct = accountById.get(position.accountId);
      return positionMatchesTierFilter(position, acct, tierFilter);
    },
    [accountById, selectedAccountId, tierFilter],
  );

  const filteredRecommendations = useMemo(
    () => recommendations.filter(matchesScopeAndTier),
    [recommendations, matchesScopeAndTier],
  );
  const filteredPending = useMemo(
    () => pendingPositions.filter(matchesScopeAndTier),
    [pendingPositions, matchesScopeAndTier],
  );
  const filteredActive = useMemo(
    () => activePositions.filter(matchesScopeAndTier),
    [activePositions, matchesScopeAndTier],
  );

  const focusAccount = useMemo(() => {
    if (selectedAccountId) {
      return accounts.find((a) => a.id === selectedAccountId) ?? accounts[0];
    }
    return accounts[0];
  }, [accounts, selectedAccountId]);

  const tierBudgetsForFocus: TierBudgetSnapshot[] = useMemo(() => {
    if (!accountContext || accountContext.accountId !== focusAccount?.id) {
      return [];
    }
    return accountContext.tiers
      .filter((t) => t.budget)
      .map((t) => t.budget!);
  }, [accountContext, focusAccount?.id]);

  const tierSpectrumRows = useMemo(() => {
    if (!focusAccount) return [];
    const combined = [
      ...recommendations,
      ...pendingPositions,
      ...activePositions,
    ].filter((p) => p.accountId === focusAccount.id);
    return buildTierSpectrum(focusAccount, tierBudgetsForFocus, combined);
  }, [
    focusAccount,
    tierBudgetsForFocus,
    recommendations,
    pendingPositions,
    activePositions,
  ]);

  const loadAccountPerformanceHistory = async (accountsToLoad: Account[]) => {
    if (accountsToLoad.length === 0) return;

    setPerformanceLoading(true);
    try {
      // Fetch performance history for all accounts
      const performancePromises = accountsToLoad.map(async (account) => {
        try {
          // Fetch activity logs with ACCOUNT_PERFORMANCE_SNAPSHOT type
          const activityLogs = await apiService.getActivityLogs(
            account.id,
            100, // Get last 100 performance snapshots
            undefined, // No specific position filter
            'ACCOUNT_PERFORMANCE_SNAPSHOT', // Filter by performance snapshot type
          );

          return activityLogs.map((log: any) => ({
            timestamp: log.createdAt,
            accountId: account.id,
            accountNickname: account.nickname,
            totalValue: log.details?.balances?.totalValue || 0,
            utilization: log.details?.utilizationPct || 0,
            positionCount: log.details?.positionCount || 0,
            totalUnrealizedPL: log.details?.totalUnrealizedPL || 0,
            cash: log.details?.balances?.cash || 0,
            buyingPower: log.details?.balances?.buyingPower || 0,
            source: log.source || 'UNKNOWN',
          }));
        } catch (error) {
          console.warn(
            `Failed to fetch performance history for account ${account.id}:`,
            error,
          );
          return [];
        }
      });

      const allPerformanceData = await Promise.all(performancePromises);
      const flattenedData = allPerformanceData.flat();

      // Sort by timestamp (oldest first)
      const sortedData = flattenedData.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );

      // Store daily aggregated data (last 50 data points for daily charts)
      const dailyData = sortedData.slice(-50);
      setAccountPerformanceHistory(dailyData);
    } catch (error) {
      console.error('Failed to load account performance history:', error);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const loadIntradayPerformanceSeries = async (
    accountsToLoad: Account[],
    timeframe: '1d' | '1w' | '1m' | '1y' | 'custom',
    customRange?: { from: string; to: string } | null,
  ) => {
    if (accountsToLoad.length === 0) return;
    if (timeframe === 'custom' && (!customRange?.from || !customRange?.to)) {
      return;
    }

    setPerformanceLoading(true);
    try {
      const seriesPromises = accountsToLoad.map(async (account) => {
        try {
          const resp =
            timeframe === 'custom' && customRange
              ? await apiService.getAccountPerformanceHistory(account.id, {
                  from: `${customRange.from}T00:00:00.000Z`,
                  to: `${customRange.to}T23:59:59.999Z`,
                })
              : await apiService.getAccountPerformanceHistory(account.id, {
                  timeframe: timeframe as '1d' | '1w' | '1m' | '1y',
                });

          return (resp.points || []).map((p) => ({
            timestamp: p.timestamp,
            accountId: account.id,
            accountNickname: account.nickname,
            totalValue: p.totalValue || 0,
            utilization: p.utilization || 0,
            positionCount: p.positionCount || 0,
            totalUnrealizedPL: p.totalUnrealizedPL || 0,
          }));
        } catch (error) {
          // Intentionally swallow so one account doesn't break the dashboard.
          return [];
        }
      });

      const allSeries = await Promise.all(seriesPromises);
      const flattened = allSeries.flat();
      const sorted = flattened.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );

      setIntradayPerformanceData(sorted);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const loadMatchingArticles = useCallback(async () => {
    if (activePositions.length === 0) {
      setMatchingArticles([]);
      return;
    }

    setArticlesLoading(true);
    try {
      // Get unique ticker symbols from active positions
      const tickerSymbols = Array.from(
        new Set(activePositions.map((pos) => pos.symbol)),
      );

      if (tickerSymbols.length === 0) {
        setMatchingArticles([]);
        return;
      }

      // Fetch articles filtered by ticker symbols
      const response = await apiService.getMatchingArticles(tickerSymbols);
      console.log('Articles API response:', response);
      setMatchingArticles(response.articles || []);
    } catch (error) {
      console.error('Failed to load matching articles:', error);
      setMatchingArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  }, [activePositions]);

  // Load performance history whenever accounts change
  useEffect(() => {
    if (accounts.length > 0) {
      loadAccountPerformanceHistory(accounts);
    }
  }, [accounts]);

  // Load intraday chart series whenever timeframe changes (or accounts first load)
  useEffect(() => {
    if (accounts.length > 0) {
      loadIntradayPerformanceSeries(
        accounts,
        intradayTimeframe,
        appliedCustomRange,
      );
    }
  }, [accounts, intradayTimeframe, appliedCustomRange]);

  // Load matching articles whenever active positions change
  useEffect(() => {
    loadMatchingArticles();
  }, [loadMatchingArticles]);

  useEffect(() => {
    setLoading(true);
    loadData();

    // Immediately refresh Schwab auth status on load
    const refreshAuthStatus = async () => {
      try {
        const status = await apiService.getSchwabAuthStatus();
        setSchwabAuthStatus(status);
      } catch (error) {
        setSchwabAuthStatus({
          authenticated: false,
          tokenExpired: false,
        });
      }
    };
    refreshAuthStatus();

    // Set up periodic refresh for Schwab auth status every 30 seconds
    const authStatusInterval = setInterval(async () => {
      try {
        const status = await apiService.getSchwabAuthStatus();
        setSchwabAuthStatus(status);
      } catch (error) {
        setSchwabAuthStatus({
          authenticated: false,
          tokenExpired: false,
        });
      }
    }, 30000);

    // Check auth status when window regains focus (user returns from OAuth)
    const handleFocus = () => {
      apiService
        .getSchwabAuthStatus()
        .then((status) => setSchwabAuthStatus(status))
        .catch(() =>
          setSchwabAuthStatus({
            authenticated: false,
            tokenExpired: false,
          }),
        );
    };

    window.addEventListener('focus', handleFocus);

    // Subscribe to real-time updates
    const unsubscribeAccount = onAccountUpdate((data) => {
      setAccounts((prev) =>
        prev.map((acc) => (acc.id === data.id ? { ...acc, ...data } : acc)),
      );
    });
    const unsubscribeBalance = onBalanceChange((data) => {
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === data.accountId ? { ...acc, balances: data.balances } : acc,
        ),
      );
    });

    return () => {
      clearInterval(authStatusInterval);
      window.removeEventListener('focus', handleFocus);
      unsubscribeAccount();
      unsubscribeBalance();
    };
  }, [onAccountUpdate, onBalanceChange]);

  // Poll account activity statuses every 10s while tab is visible.
  useEffect(() => {
    let intervalId: number | null = null;
    let cancelled = false;

    const pollOnce = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!accounts || accounts.length === 0) return;

      try {
        const results = await Promise.all(
          accounts.map(async (account) => {
            const activities = await apiService.getAccountActivityStatus(
              account.id,
            );
            return { accountId: account.id, activities };
          }),
        );

        if (cancelled) return;

        const next: Record<string, AccountActivityStatus[]> = {};
        for (const r of results) next[r.accountId] = r.activities || [];
        setActivityStatusesByAccount(next);
      } catch (error) {
        // Intentionally swallow: activity status should never break the dashboard.
      }
    };

    const start = () => {
      if (intervalId) window.clearInterval(intervalId);
      intervalId = window.setInterval(pollOnce, 10000);
    };

    const stop = () => {
      if (intervalId) window.clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollOnce();
        start();
      } else {
        stop();
      }
    };

    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accounts]);

  const loadPositions = async () => {
    setPositionsLoading(true);
    await loadData();
    setPositionsLoading(false);
  };

  const loadData = async () => {
    try {
      const [accountsData, , schwabStatus] = await Promise.all([
        apiService.getAccounts(),
        apiService.getUtilizationSummary(),
        apiService.getSchwabAuthStatus().catch(() => ({
          authenticated: false,
          tokenExpired: false,
        })),
      ]);

      setAccounts(accountsData);
      setSchwabAuthStatus(schwabStatus);

      // Load active positions for all accounts
      if (accountsData.length > 0) {
        const activePositionsPromises = accountsData.map(async (account) => {
          try {
            // Use the new paginated endpoint with MongoDB account ID and status filter
            const positionsData = await apiService.getAccountPositionsPaginated(
              account.id, // MongoDB account ID
              {
                status: 'ACTIVE',
                limit: 100, // Get up to 100 active positions per account
              },
            );

            return positionsData.positions;
          } catch (error) {
            console.warn(
              `Failed to fetch positions for account ${account.accountId}:`,
              error,
            );
            return [];
          }
        });

        const allActivePositions = await Promise.all(activePositionsPromises);
        const flattenedPositions = allActivePositions.flat();
        setActivePositions(flattenedPositions);

        const orphanResults = await Promise.all(
          accountsData.map(async (account) => {
            try {
              const report = await apiService.getHoldingsOrphans(account.id);
              return (report.schwabOnly || []).map((orphan) => ({
                accountId: account.id,
                accountNickname: account.nickname || account.accountId,
                symbol: orphan.symbol,
                longQuantity: orphan.longQuantity,
                shortQuantity: orphan.shortQuantity,
                marketValue: orphan.marketValue,
                isShort: orphan.isShort,
              }));
            } catch (error) {
              console.warn(
                `Failed to fetch holdings orphans for account ${account.id}:`,
                error,
              );
              return [] as DashboardSchwabOrphan[];
            }
          }),
        );
        setSchwabOrphans(orphanResults.flat());

        // Fetch activity logs for each position using the new filtered endpoint
        const activityLogPromises = flattenedPositions.map(async (position) => {
          try {
            // Use the new activity logs endpoint with positionId and activityType filters
            const activityLogs = await apiService.getActivityLogs(
              position.accountId,
              50,
              position._id, // Filter by specific position ID
              undefined, // Filter by health check activity type
            );

            console.log(
              `🔍 Activity logs for position ${position.symbol} (${position._id}):`,
              {
                totalLogs: activityLogs.length,
                sampleLogs: activityLogs.slice(0, 3), // Show first 3 logs
                positionId: position._id,
                accountId: position.accountId,
              },
            );

            // Since we're filtering on the backend, we just need to map the data
            const mappedLogs = activityLogs
              .filter(
                (log: any) =>
                  log.details?.healthCheck?.totalReturn !== undefined,
              )
              .map((log: any) => ({
                timestamp: log.createdAt,
                totalReturn: log.details.healthCheck.totalReturn,
                dollarReturn: log.details.healthCheck.dollarReturn,
              }))
              .sort(
                (a, b) =>
                  new Date(a.timestamp).getTime() -
                  new Date(b.timestamp).getTime(),
              ); // Sort chronologically (oldest first)

            console.log(`✅ Mapped activity logs for ${position.symbol}:`, {
              mappedCount: mappedLogs.length,
              mappedLogs: mappedLogs,
            });

            return {
              positionId: position._id,
              logs: mappedLogs,
            };
          } catch (error) {
            console.warn(
              `Failed to fetch activity logs for position ${position._id}:`,
              error,
            );
            return { positionId: position._id, logs: [] };
          }
        });

        const allActivityLogs = await Promise.all(activityLogPromises);
        const activityLogMap = allActivityLogs.reduce(
          (acc, { positionId, logs }) => {
            acc[positionId] = logs;
            return acc;
          },
          {} as Record<string, any[]>,
        );
        setPositionActivityLogs(activityLogMap);

        // Debug logging for activity logs
        console.log('📈 Activity logs summary:', {
          totalPositions: flattenedPositions.length,
          activityLogMap: activityLogMap,
          positionsWithLogs: Object.keys(activityLogMap).length,
          sampleLogs: Object.entries(activityLogMap).slice(0, 2), // Show first 2 positions' logs
        });

        // Fetch AI risk assessment reviews for each position
        const aiReviewPromises = flattenedPositions.map(async (position) => {
          try {
            const aiReviews = await apiService.getActivityLogs(
              position.accountId,
              1, // Limit to 1 (most recent)
              position._id, // Filter by specific position ID
              'AI_RISK_ASSESSMENT', // Filter by AI risk assessment activity type
            );

            console.log(
              `🤖 AI reviews for position ${position.symbol} (${position._id}):`,
              {
                totalReviews: aiReviews.length,
                latestReview: aiReviews[0],
                positionId: position._id,
                accountId: position.accountId,
              },
            );

            return {
              positionId: position._id,
              review: aiReviews.length > 0 ? aiReviews[0] : null,
            };
          } catch (error) {
            console.warn(
              `Failed to fetch AI reviews for position ${position._id}:`,
              error,
            );
            return { positionId: position._id, review: null };
          }
        });

        const allAiReviews = await Promise.all(aiReviewPromises);
        const aiReviewMap = allAiReviews.reduce(
          (acc, { positionId, review }) => {
            acc[positionId] = review;
            return acc;
          },
          {} as Record<string, any>,
        );
        setPositionAiReviews(aiReviewMap);

        // Debug logging for AI reviews
        console.log('🤖 AI reviews summary:', {
          totalPositions: flattenedPositions.length,
          aiReviewMap: aiReviewMap,
          positionsWithReviews: Object.values(aiReviewMap).filter(
            (review) => review !== null,
          ).length,
          sampleReviews: Object.entries(aiReviewMap).slice(0, 2), // Show first 2 positions' reviews
        });

        // Fetch current prices for all unique symbols
        if (flattenedPositions.length > 0) {
          const uniqueSymbols = Array.from(
            new Set(flattenedPositions.map((pos) => pos.symbol)),
          );

          try {
            const quotesData = await apiService.getSchwabQuotes(uniqueSymbols);
            const priceMap: Record<string, number> = {};

            // Extract lastPrice from each quote
            Object.entries(quotesData).forEach(
              ([symbol, quote]: [string, any]) => {
                if (quote && quote.lastPrice !== undefined) {
                  priceMap[symbol] = quote.lastPrice;
                }
              },
            );

            setPositionPrices(priceMap);
          } catch (error) {
            console.warn('Failed to fetch current prices:', error);
            // Continue without prices - PositionCard will handle missing prices gracefully
          }
        }

        // Fetch recommended positions for all accounts (status=RECOMMENDED)
        const allRecommendedPositions: AccountPosition[] = [];
        for (const account of accountsData) {
          try {
            const positionsData = await apiService.getAccountPositionsPaginated(
              account.id,
              {
                status: 'RECOMMENDED',
                limit: 20, // Get up to 20 recommended positions per account
              },
            );
            allRecommendedPositions.push(...positionsData.positions);
          } catch (error) {
            console.warn(
              `Failed to fetch recommended positions for account ${account.id}:`,
              error,
            );
          }
        }

        // Sort recommended positions by creation date (newest first) and take top 10
        const sortedRecommendedPositions = allRecommendedPositions
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .sort((a, b) => (a.rank || 0) - (b.rank || 0))
          .slice(0, 10);

        setRecommendations(sortedRecommendedPositions);

        // Fetch pending positions (APPROVED, ENTRY_PENDING, PENDING_MARKET_OPEN) for all accounts
        // These are positions that are between RECOMMENDED and ACTIVE status
        const allPendingPositions: AccountPosition[] = [];
        const pendingStatuses = [
          'APPROVED',
          'ENTRY_PENDING',
          'PENDING_MARKET_OPEN',
        ];

        for (const account of accountsData) {
          for (const status of pendingStatuses) {
            try {
              const positionsData =
                await apiService.getAccountPositionsPaginated(account.id, {
                  status: status,
                  limit: 50, // Get up to 50 positions per status per account
                });
              allPendingPositions.push(...positionsData.positions);
            } catch (error) {
              console.warn(
                `Failed to fetch ${status} positions for account ${account.id}:`,
                error,
              );
            }
          }
        }

        // Sort pending positions by creation date (newest first)
        const sortedPendingPositions = allPendingPositions.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        setPendingPositions(sortedPendingPositions);
      } else {
        setActivePositions([]);
        setSchwabOrphans([]);
        setRecommendations([]);
        setPendingPositions([]);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalValue = accounts.reduce(
    (sum, account) => sum + (account.balances?.totalValue ?? 0),
    0,
  );
  const totalOptionBuyingPower = accounts.reduce(
    (sum, account) => sum + (account.balances?.optionBuyingPower ?? 0),
    0,
  );
  const totalStockBuyingPower = accounts.reduce(
    (sum, account) => sum + (account.balances?.stockBuyingPower ?? 0),
    0,
  );

  // Defensive formatting for NaN
  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString()}`;
  };

  // Process account performance history data for charts
  const processPerformanceData = () => {
    if (accountPerformanceHistory.length === 0) {
      // Return mock data if no real data is available
      return [
        {
          date: '2024-06-01',
          value: 10000,
          utilization: 0.2,
          pnl: 0,
          positionCount: 0,
        },
        {
          date: '2024-06-08',
          value: 12000,
          utilization: 0.3,
          pnl: 500,
          positionCount: 2,
        },
        {
          date: '2024-06-15',
          value: 14000,
          utilization: 0.4,
          pnl: 1200,
          positionCount: 3,
        },
        {
          date: '2024-06-22',
          value: 13500,
          utilization: 0.35,
          pnl: 900,
          positionCount: 2,
        },
        {
          date: '2024-06-29',
          value: 15000,
          utilization: 0.5,
          pnl: 2000,
          positionCount: 4,
        },
      ];
    }

    // Group data by date and aggregate values
    const dateGroups = new Map();

    accountPerformanceHistory.forEach((dataPoint) => {
      const date = new Date(dataPoint.timestamp).toISOString().split('T')[0];

      if (!dateGroups.has(date)) {
        dateGroups.set(date, {
          date,
          value: 0,
          utilization: 0,
          pnl: 0,
          positionCount: 0,
          dataPoints: 0,
        });
      }

      const group = dateGroups.get(date);
      group.value += dataPoint.totalValue;
      group.utilization += dataPoint.utilization;
      group.pnl += dataPoint.totalUnrealizedPL;
      group.positionCount += dataPoint.positionCount;
      group.dataPoints += 1;
    });

    // Average the values and convert to chart format
    return Array.from(dateGroups.values())
      .map((group) => ({
        date: group.date,
        value: Math.round(group.value / group.dataPoints),
        utilization:
          Math.round((group.utilization / group.dataPoints) * 100) / 100,
        pnl: Math.round(group.pnl / group.dataPoints),
        positionCount: Math.round(group.positionCount / group.dataPoints),
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const performanceChartData = processPerformanceData();

  // Process intraday data for real-time chart
  const getChartFormatMode = (): '1d' | '1w' | '1m' | '1y' => {
    if (intradayTimeframe !== 'custom') {
      return intradayTimeframe;
    }

    if (!appliedCustomRange) {
      return '1m';
    }

    const fromMs = new Date(`${appliedCustomRange.from}T00:00:00.000Z`).getTime();
    const toMs = new Date(`${appliedCustomRange.to}T23:59:59.999Z`).getTime();
    const windowMs = Math.max(toMs - fromMs, 0);
    const dayMs = 24 * 60 * 60 * 1000;

    if (windowMs <= 2 * dayMs) return '1d';
    if (windowMs <= 14 * dayMs) return '1w';
    if (windowMs <= 90 * dayMs) return '1m';
    return '1y';
  };

  const formatIntradayTimestampTick = (
    isoTimestamp: string,
    timeframe: '1d' | '1w' | '1m' | '1y',
  ) => {
    const d = new Date(isoTimestamp);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yy = String(d.getUTCFullYear()).slice(-2);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');

    switch (timeframe) {
      case '1d':
        return `${hh}:${min}`;
      case '1w':
        return `${mm}/${dd} ${hh}:00`;
      case '1m':
        return `${mm}/${dd}`;
      case '1y':
        return `${mm}/${dd}/${yy}`;
    }
  };

  const formatIntradayTooltipLabel = (
    isoTimestamp: string,
    timeframe: '1d' | '1w' | '1m' | '1y',
  ) => {
    const d = new Date(isoTimestamp);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yy = String(d.getUTCFullYear()).slice(-2);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');

    switch (timeframe) {
      case '1d':
        return `${mm}/${dd} ${hh}:${min}`;
      case '1w':
        return `${mm}/${dd} ${hh}:00`;
      case '1m':
        return `${mm}/${dd}`;
      case '1y':
        return `${mm}/${dd}/${yy}`;
    }
  };

  const chartFormatMode = getChartFormatMode();

  const processIntradayData = () => {
    if (intradayPerformanceData.length === 0) {
      // Return mock intraday data if no real data is available
      const mockIntradayData = [];
      const now = new Date();
      for (let i = 0; i < 20; i++) {
        const time = new Date(now.getTime() - (19 - i) * 15 * 60 * 1000); // 15-minute intervals
        mockIntradayData.push({
          timestamp: time.toISOString(),
          time: formatIntradayTimestampTick(time.toISOString(), '1d'),
          value: 10000 + Math.random() * 2000 - 1000,
          utilization: 0.2 + Math.random() * 0.3,
          pnl: Math.random() * 1000 - 500,
        });
      }
      return mockIntradayData;
    }

    // Convert raw data to chart format with time labels
    return intradayPerformanceData.map((dataPoint) => ({
      timestamp: dataPoint.timestamp,
      time: formatIntradayTimestampTick(dataPoint.timestamp, chartFormatMode),
      value: dataPoint.totalValue,
      utilization: dataPoint.utilization,
      pnl: dataPoint.totalUnrealizedPL,
      positionCount: dataPoint.positionCount,
      accountNickname: dataPoint.accountNickname,
    }));
  };

  const intradayChartData = processIntradayData();

  // Calculate performance metrics
  const calculatePerformanceMetrics = () => {
    if (accountPerformanceHistory.length === 0) {
      return {
        totalReturn: 0,
        totalReturnPercent: 0,
        avgUtilization: 0,
        maxUtilization: 0,
        currentPositionCount: 0,
        dataPoints: 0,
      };
    }

    const latest =
      accountPerformanceHistory[accountPerformanceHistory.length - 1];
    const earliest = accountPerformanceHistory[0];

    const totalReturn = latest.totalValue - earliest.totalValue;
    const totalReturnPercent =
      earliest.totalValue > 0 ? (totalReturn / earliest.totalValue) * 100 : 0;

    const avgUtilization =
      accountPerformanceHistory.reduce((sum, dp) => sum + dp.utilization, 0) /
      accountPerformanceHistory.length;
    const maxUtilization = Math.max(
      ...accountPerformanceHistory.map((dp) => dp.utilization),
    );

    return {
      totalReturn,
      totalReturnPercent,
      avgUtilization: Math.round(avgUtilization * 100) / 100,
      maxUtilization: Math.round(maxUtilization * 100) / 100,
      currentPositionCount: latest.positionCount,
      dataPoints: accountPerformanceHistory.length,
    };
  };

  const performanceMetrics = calculatePerformanceMetrics();

  // Position card action handlers
  const handleViewDetails = (position: AccountPosition) => {
    setSelectedPosition(position);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPosition(null);
  };

  const handleViewRecommendationDetails = (recommendation: AccountPosition) => {
    setSelectedRecommendation(recommendation);
    setIsRecommendationModalOpen(true);
  };

  const handleCloseRecommendationModal = () => {
    setIsRecommendationModalOpen(false);
    setSelectedRecommendation(null);
  };

  const handleCloseFull = (position: AccountPosition) => {
    setPositionToClose(position);
    setIsCloseConfirmationOpen(true);
  };

  const handleCloseSchwabOrphan = async (orphan: DashboardSchwabOrphan) => {
    const qty = orphan.isShort ? orphan.shortQuantity : orphan.longQuantity;
    const confirmed = window.confirm(
      `Close Schwab-only orphan on MARKET?\n\n${orphan.symbol}\n${orphan.isShort ? 'SHORT' : 'LONG'} × ${qty}\nAccount: ${orphan.accountNickname || orphan.accountId}\n\nThis places a broker close without creating a Marlin position.`,
    );
    if (!confirmed) return;

    const key = `${orphan.accountId}:${orphan.symbol}`;
    setClosingOrphanKey(key);
    setOrphanActionMessage(null);
    try {
      const result = await apiService.closeHoldingsOrphan(
        orphan.accountId,
        orphan.symbol,
      );
      if (result.success) {
        setOrphanActionMessage(
          `Close submitted for ${orphan.symbol} (order ${result.schwabOrderId}).`,
        );
      } else {
        setOrphanActionMessage(
          result.errorMessage ||
            result.message ||
            `Close failed for ${orphan.symbol}.`,
        );
      }
      await loadData();
    } catch (error: any) {
      setOrphanActionMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          `Close failed for ${orphan.symbol}.`,
      );
    } finally {
      setClosingOrphanKey(null);
    }
  };

  const handleConfirmClose = async () => {
    if (!positionToClose) return;

    setUpdating(positionToClose._id);
    try {
      await apiService.cancelPosition(positionToClose._id);
      // Refresh the data to show updated position status
      await loadData();
    } catch (error) {
      console.error('Error closing position:', error);
    } finally {
      setUpdating(null);
      setIsCloseConfirmationOpen(false);
      setPositionToClose(null);
    }
  };

  const handleCancelClose = () => {
    setIsCloseConfirmationOpen(false);
    setPositionToClose(null);
  };

  const handleCloseHalf = (position: AccountPosition) => {
    // TODO: Implement close half position
    console.log('Close half position:', position.symbol);
  };

  const handleApproveRecommendation = async (recommendationId: string) => {
    setUpdating(recommendationId);
    try {
      await apiService.updatePositionStatus(recommendationId, 'APPROVED');
      await loadData();
    } catch (error) {
      console.error('Error approving recommendation:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleRejectRecommendation = async (recommendationId: string) => {
    setUpdating(recommendationId);
    try {
      // Prompt for rejection reason (optional)
      // const notes = window.prompt('Enter rejection reason (optional):');

      await apiService.updatePositionStatus(
        recommendationId,
        'REJECTED',
        undefined,
      );
      await loadData();
    } catch (error) {
      console.error('Error rejecting recommendation:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleCancelPendingPosition = async (positionId: string) => {
    setUpdating(positionId);
    try {
      await apiService.cancelPosition(positionId);
      await loadData();
    } catch (error) {
      console.error('Error canceling pending position:', error);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Portfolio command center</h1>
        <p className="text-gray-600">
          Capital, thesis, and positions organized by tier objective
        </p>
      </div>

      <TierPageBanner
        title="Active tier filter"
        description="Use the tier rail above to filter every section on this page. Enabled slots (typically Tier 1 macro and Tier 6 options) show live budgets and deployment."
        settingsHint
      />

      {accounts.length > 0 ? <DashboardTierHero /> : null}

      {/* Schwab Connection Status */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {schwabAuthStatus?.authenticated &&
            !schwabAuthStatus.tokenExpired ? (
              <ShieldCheck className="w-5 h-5 text-green-500" />
            ) : (
              <ShieldOff className="w-5 h-5 text-red-500" />
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Schwab Connection
              </h3>
              <p className="text-sm text-gray-500">
                {schwabAuthStatus?.authenticated &&
                !schwabAuthStatus.tokenExpired
                  ? 'Connected and active'
                  : 'Not connected'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={async () => {
                try {
                  const status = await apiService.getSchwabAuthStatus();
                  setSchwabAuthStatus(status);
                } catch (error) {
                  setSchwabAuthStatus({
                    authenticated: false,
                    tokenExpired: false,
                  });
                }
              }}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </button>
            <a
              href="/app/settings"
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Settings className="w-4 h-4 mr-2" />
              {schwabAuthStatus?.authenticated && !schwabAuthStatus.tokenExpired
                ? 'Manage'
                : 'Connect'}
            </a>
          </div>
        </div>
        {schwabAuthStatus && !schwabAuthStatus.authenticated && !loading && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Action Required:</strong> Connect your Schwab account in
              Settings to access trading data and account information.
            </p>
          </div>
        )}
      </div>

      {/* Account Activity Status */}
      {accounts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Account Activity
            </h2>
            <span className="text-sm text-gray-500">Updates every 10s</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {account.nickname}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {account.accountNumber
                        ? `•••• ${account.accountNumber.slice(-4)}`
                        : account.accountId}
                    </div>
                  </div>
                </div>
                <ActivityStatusIndicator
                  activities={activityStatusesByAccount[account.id] || []}
                  maxActivities={3}
                />
                <div className="mt-3">
                  <Link
                    to={`/app/accounts/${account.id}`}
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    Open account
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: Market Thesis */}
      <div className="mb-8">
        <TierThesisWrapper />
      </div>

      {/* Section 4: Position Suggestions by tier */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Position suggestions by tier
          </h2>
          <button
            onClick={loadData}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh recommendations"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {tierSpectrumRows.map((row) => {
          const tierRecs = filteredRecommendations.filter(
            (p) =>
              resolveRiskTierForPosition(
                p,
                accountById.get(p.accountId),
              ) === row.riskTier,
          );
          if (tierFilter !== 'all' && tierFilter !== row.riskTier) return null;
          return (
            <TierPositionSection
              key={`rec-${row.riskTier}`}
              row={row}
              accountId={focusAccount?.id}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tierRecs.map((recommendation) => (
                  <RecommendationCard
                    key={recommendation._id}
                    recommendation={recommendation}
                    account={accountById.get(recommendation.accountId)}
                    onApprove={handleApproveRecommendation}
                    onReject={handleRejectRecommendation}
                    onViewDetails={handleViewRecommendationDetails}
                    isUpdating={updating === recommendation._id}
                  />
                ))}
              </div>
            </TierPositionSection>
          );
        })}
      </div>

      {/* Section 4.5: Pending Positions by tier */}
      {filteredPending.length > 0 && (
        <div className="mb-8 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Pending positions by tier
          </h2>
          {tierSpectrumRows.map((row) => {
            const tierPending = filteredPending.filter(
              (p) =>
                resolveRiskTierForPosition(
                  p,
                  accountById.get(p.accountId),
                ) === row.riskTier,
            );
            if (!tierPending.length) return null;
            if (tierFilter !== 'all' && tierFilter !== row.riskTier) return null;
            return (
              <TierPositionSection
                key={`pend-${row.riskTier}`}
                row={row}
                accountId={focusAccount?.id}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {tierPending.map((position) => (
              <div
                key={position._id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {position.symbol}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">
                      {position.status?.toLowerCase().replace(/_/g, ' ')}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                    {position.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  {position.allocatedCapital && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Allocated:</span>
                      <span className="font-medium">
                        {formatCurrency(position.allocatedCapital)}
                      </span>
                    </div>
                  )}
                  {position.tradeDirection && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Direction:</span>
                      <span className="font-medium">
                        {position.tradeDirection}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(position)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleCancelPendingPosition(position._id)}
                    disabled={updating === position._id}
                    className="flex-1 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating === position._id ? 'Canceling...' : 'Cancel'}
                  </button>
                </div>
              </div>
                  ))}
                </div>
              </TierPositionSection>
            );
          })}
        </div>
      )}

      {/* Section 1: Current Positions by tier */}
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Current positions by tier
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {filteredActive.length} active
              {schwabOrphans.length > 0
                ? ` · ${schwabOrphans.length} orphan${schwabOrphans.length !== 1 ? 's' : ''}`
                : ''}
            </span>
            <button
              onClick={loadPositions}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh positions"
            >
              <RefreshCw
                className={`w-4 h-4 ${positionsLoading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>

        {schwabOrphans.length > 0 ? (
          <div className="mb-4 flex items-start gap-3 rounded-lg border-2 border-amber-500 bg-amber-50 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {schwabOrphans.length} Schwab orphan
                {schwabOrphans.length !== 1 ? 's' : ''} need attention
              </p>
              <p className="mt-1 text-sm text-amber-800">
                These holdings are on Schwab but not tracked by Marlin. Close
                them from the highlighted cards below.
              </p>
            </div>
          </div>
        ) : null}

        {orphanActionMessage ? (
          <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            {orphanActionMessage}
          </div>
        ) : null}

        {filteredActive.length === 0 && schwabOrphans.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Wallet className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No Active Positions
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have any active positions at the moment.
            </p>
          </div>
        ) : (
          <>
            {tierSpectrumRows.map((row) => {
              const tierActive = filteredActive.filter(
                (p) =>
                  resolveRiskTierForPosition(
                    p,
                    accountById.get(p.accountId),
                  ) === row.riskTier,
              );
              if (!tierActive.length) return null;
              if (tierFilter !== 'all' && tierFilter !== row.riskTier) {
                return null;
              }
              return (
                <TierPositionSection
                  key={`active-${row.riskTier}`}
                  row={row}
                  accountId={focusAccount?.id}
                >
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {tierActive.map((position) => (
                      <PositionCard
                        key={position._id}
                        position={position}
                        account={accountById.get(position.accountId)}
                        activityLogData={positionActivityLogs[position._id] || []}
                        aiReviewData={positionAiReviews[position._id] || null}
                        currentPrice={positionPrices[position.symbol]}
                        updating={updating}
                        onViewDetails={handleViewDetails}
                        onCloseFull={handleCloseFull}
                        onCloseHalf={handleCloseHalf}
                      />
                    ))}
                  </div>
                </TierPositionSection>
              );
            })}
            {schwabOrphans.length > 0 ? (
              <TierPositionSection
                row={{
                  riskTier: 1,
                  catalog: TIER_CATALOG[1],
                  slot: null,
                  enabled: false,
                  budget: null,
                  positionCounts: {
                    active: schwabOrphans.length,
                    pending: 0,
                    recommendations: 0,
                  },
                  deepLink: focusAccount
                    ? `/app/accounts/${focusAccount.id}?tier=1`
                    : '/app/dashboard',
                }}
                emptyMessage=""
                showPerformanceChart={false}
              >
                <p className="mb-4 text-sm text-amber-800">
                  Schwab holdings outside Marlin tier registry
                </p>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {schwabOrphans.map((orphan) => (
                    <SchwabOrphanCard
                      key={`${orphan.accountId}:${orphan.symbol}`}
                      orphan={orphan}
                      closing={
                        closingOrphanKey === `${orphan.accountId}:${orphan.symbol}`
                      }
                      onClose={handleCloseSchwabOrphan}
                    />
                  ))}
                </div>
              </TierPositionSection>
            ) : null}
          </>
        )}
      </div>

      {/* Section 2: Account Performance History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Account Performance History
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {performanceMetrics.dataPoints} data points
            </span>
            <button
              onClick={() => loadAccountPerformanceHistory(accounts)}
              disabled={performanceLoading}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              title="Refresh performance data"
            >
              <RefreshCw
                className={`w-4 h-4 ${performanceLoading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>

        {performanceLoading ? (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Loading Performance Data
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Fetching account performance history...
            </p>
          </div>
        ) : accountPerformanceHistory.length === 0 ? (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No Performance Data Available
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Performance data will appear here once account monitoring and sync
              processes begin collecting data.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Performance Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Wallet className="h-8 w-8 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Value
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalValue)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp
                      className={`h-8 w-8 ${performanceMetrics.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Return
                    </p>
                    <p
                      className={`text-2xl font-semibold ${performanceMetrics.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {formatCurrency(performanceMetrics.totalReturn)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {performanceMetrics.totalReturnPercent >= 0 ? '+' : ''}
                      {performanceMetrics.totalReturnPercent.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <PieIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Avg Utilization
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {(performanceMetrics.avgUtilization * 100).toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500">
                      Max:{' '}
                      {(performanceMetrics.maxUtilization * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div> */}

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {/* Icon of your choice */}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Option Buying Power
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalOptionBuyingPower)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {/* Icon of your choice */}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Stock Buying Power
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalStockBuyingPower)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Legacy Key Metrics - Keeping for now */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-8 w-8 text-success-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Market Value
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalMarketValue)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Target className="h-8 w-8 text-warning-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Cash Balance
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalCash)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-8 w-8 text-danger-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Active Accounts
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {accounts.filter((acc) => acc.isActive).length}
                    </p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Performance Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Intraday Account Value Movement */}
              <div className="card lg:col-span-2">
                <div className="flex flex-col gap-3 mb-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="inline-flex rounded-md bg-gray-100 p-1">
                      {[
                        { label: '1D', value: '1d' as const },
                        { label: '1W', value: '1w' as const },
                        { label: '1M', value: '1m' as const },
                        { label: '1Y', value: '1y' as const },
                        { label: 'Custom', value: 'custom' as const },
                      ].map((t) => (
                        <button
                          key={t.value}
                          onClick={() => {
                            if (t.value === 'custom') {
                              setIntradayTimeframe('custom');
                              if (!appliedCustomRange) {
                                setAppliedCustomRange({
                                  from: customFromDate,
                                  to: customToDate,
                                });
                              }
                              return;
                            }
                            setIntradayTimeframe(t.value);
                          }}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${
                            intradayTimeframe === t.value
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {intradayTimeframe === 'custom' && (
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            From
                          </label>
                          <input
                            type="date"
                            value={customFromDate}
                            max={customToDate}
                            onChange={(e) => setCustomFromDate(e.target.value)}
                            className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            To
                          </label>
                          <input
                            type="date"
                            value={customToDate}
                            min={customFromDate}
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setCustomToDate(e.target.value)}
                            className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={
                            !customFromDate ||
                            !customToDate ||
                            customFromDate > customToDate ||
                            (appliedCustomRange?.from === customFromDate &&
                              appliedCustomRange?.to === customToDate)
                          }
                          onClick={() =>
                            setAppliedCustomRange({
                              from: customFromDate,
                              to: customToDate,
                            })
                          }
                          className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={intradayChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(value) =>
                        formatIntradayTimestampTick(value, chartFormatMode)
                      }
                      tick={{ fontSize: 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(value / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatCurrency(value),
                        'Total Value',
                      ]}
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0] && payload[0].payload) {
                          const data = payload[0].payload;
                          return formatIntradayTooltipLabel(
                            data.timestamp,
                            chartFormatMode,
                          );
                        }
                        return value;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={{ fill: '#2563eb', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: '#2563eb', strokeWidth: 2 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 text-sm text-gray-500 text-center">
                  Real-time account value tracking with{' '}
                  {intradayChartData.length} data points
                </div>
              </div>

              {/* Intraday Utilization Movement */}
              <div className="card lg:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <PieIcon className="w-5 h-5 mr-2" /> Intraday Utilization
                  Movement
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={intradayChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(value) =>
                        formatIntradayTimestampTick(value, chartFormatMode)
                      }
                      tick={{ fontSize: 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={[0, 1]}
                      tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${(value * 100).toFixed(1)}%`,
                        'Utilization',
                      ]}
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0] && payload[0].payload) {
                          const data = payload[0].payload;
                          return formatIntradayTooltipLabel(
                            data.timestamp,
                            chartFormatMode,
                          );
                        }
                        return value;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="utilization"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: '#10b981', strokeWidth: 2 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 text-sm text-gray-500 text-center">
                  Real-time utilization tracking with {intradayChartData.length}{' '}
                  data points
                </div>
              </div>

              {/* Account Value Over Time */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" /> Account Value Over
                  Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(value / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatCurrency(value),
                        'Total Value',
                      ]}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Utilization Over Time */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <PieIcon className="w-5 h-5 mr-2" /> Utilization Over Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <YAxis
                      domain={[0, 1]}
                      tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${(value * 100).toFixed(1)}%`,
                        'Utilization',
                      ]}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="utilization"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Position Count Over Time */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2" /> Position Count Over Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => [value, 'Position Count']}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <Bar
                      dataKey="positionCount"
                      fill="#8b5cf6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* P&L Over Time */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" /> Unrealized P&L Over
                  Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={performanceChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(value / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatCurrency(value),
                        'Unrealized P&L',
                      ]}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <Bar dataKey="pnl" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Matching Articles Feed */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <ActivitySquare className="w-5 h-5 mr-2" /> Articles for Your
            Positions
            {activePositions.length === 0 && (
              <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                No Positions
              </span>
            )}
          </h2>
          <button
            onClick={loadMatchingArticles}
            disabled={articlesLoading}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 mr-1 ${articlesLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>

        {articlesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-gray-500">Loading articles...</span>
          </div>
        ) : activePositions.length === 0 ? (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <ActivitySquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No Active Positions
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Articles will appear here when you have active positions in your
              accounts.
            </p>
          </div>
        ) : !Array.isArray(matchingArticles) ||
          matchingArticles.length === 0 ? (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <ActivitySquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No Articles Found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              No recent articles found for your position tickers:{' '}
              {Array.from(
                new Set(activePositions.map((pos) => pos.symbol)),
              ).join(', ')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 mb-4">
              Showing{' '}
              {Array.isArray(matchingArticles) ? matchingArticles.length : 0}{' '}
              recent articles for your positions:{' '}
              {Array.from(
                new Set(activePositions.map((pos) => pos.symbol)),
              ).join(', ')}
            </div>
            <div className="space-y-3">
              {Array.isArray(matchingArticles) &&
                matchingArticles.map((article) => (
                  <div
                    key={article._id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                          {article.description || article.content}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>
                            {new Date(
                              article.publishedAt || article.createdAt,
                            ).toLocaleDateString()}
                          </span>
                          {article.feedTitle && (
                            <span>• {article.feedTitle}</span>
                          )}
                          {article.tickerSymbols &&
                            article.tickerSymbols.length > 0 && (
                              <span>• {article.tickerSymbols.join(', ')}</span>
                            )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {article.marketSentiment && (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              article.marketSentiment === 'bullish'
                                ? 'bg-green-100 text-green-800'
                                : article.marketSentiment === 'bearish'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {article.marketSentiment}
                          </span>
                        )}
                        {article.expectedPriceMovement && (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              article.expectedPriceMovement === 'up'
                                ? 'bg-green-100 text-green-800'
                                : article.expectedPriceMovement === 'down'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {article.expectedPriceMovement}
                          </span>
                        )}
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-800 text-xs font-medium"
                        >
                          Read →
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Position Details Modal */}
      <PositionDetailsModal
        position={selectedPosition}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Close Position Confirmation Modal */}
      <ClosePositionConfirmationModal
        position={positionToClose}
        isOpen={isCloseConfirmationOpen}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        isClosing={updating === positionToClose?._id}
      />

      {/* Recommendation Details Modal */}
      <PositionDetailsModal
        position={selectedRecommendation}
        isOpen={isRecommendationModalOpen}
        onClose={handleCloseRecommendationModal}
      />
    </div>
  );
};

export default Dashboard;
